import React, { useState, useEffect, useCallback } from "react";
import {
    Navigate,
    useNavigate,
    useLocation,
    Routes,
    Route,
} from "react-router-dom";
import {
    Tabs,
    Tab,
    CircularProgress,
    Typography,
    Alert,
    Box,
} from "@mui/material";
import {
    getProperties,
    getRateplans,
    getRoomtypes,
    getTaxes,
    getPolicies,
    pushContentAPI,
    auditContentAPI,
} from "../../api";
import TestProperties from "./TestProperties";
import { PushApiForm, AuditApiForm } from "./ApiFormComponents";
import {
    formatJsonData,
    getObjectType,
    getEntityLabel,
    messageTypeOptions,
} from "./FormComponents";
import "./ContentAPI.css";

const ContentAPI = ({ onBack, onFinish }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const currentPath = location.pathname.split("/").pop();
    const activeTab = currentPath === "push" ? 0 : 1;

    // Get configuration data from localStorage
    const [apiConfiguration] = useState(() => {
        const stored = localStorage.getItem("apiConfiguration");
        return stored ? JSON.parse(stored) : null;
    });

    // States
    const [testProperties, setTestProperties] = useState([]);
    const [testPropertiesLoading, setTestPropertiesLoading] = useState(false);
    const [pushConfig, setPushConfig] = useState({
        messageType: "",
        updateType: "",
        property: null,
        entity: "",
    });
    const [auditConfig, setAuditConfig] = useState({
        messageType: "",
        property: null,
        entity: "",
    });
    const [propertyOptions, setPropertyOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [entityOptions, setEntityOptions] = useState([]);
    const [entityLoading, setEntityLoading] = useState(false);
    const [requestJSON, setRequestJSON] = useState("");
    const [response, setResponse] = useState("");
    const [submitLoading, setSubmitLoading] = useState(false);
    const [responseMessage, setResponseMessage] = useState({
        text: "",
        status: null,
    });

    // Filter message types based on supported messages from configuration
    const filteredMessageTypeOptions = React.useMemo(() => {
        if (!apiConfiguration?.supportedMessages) {
            return messageTypeOptions;
        }
        return messageTypeOptions.filter((option) =>
            apiConfiguration.supportedMessages.includes(option.value),
        );
    }, [apiConfiguration]);

    // Check if audit API is supported
    const isAuditAPISupported = apiConfiguration?.supportsAuditAPI === "yes";

    // Redirect to push tab if audit is not supported and user is on audit tab
    React.useEffect(() => {
        if (!isAuditAPISupported && currentPath === "audit") {
            navigate("/api-testing/content-api/push");
        }
    }, [isAuditAPISupported, currentPath, navigate]);

    // Reset entity data when messageType or property changes
    useEffect(() => {
        let currentConfig = {
            messageType: auditConfig.messageType,
            property: auditConfig.property,
        };
        if (activeTab === 0) {
            currentConfig = {
                messageType: pushConfig.messageType,
                property: pushConfig.property,
            };
        }
        setEntityOptions([]);

        if (activeTab === 0) {
            setPushConfig((prev) => ({ ...prev, entity: "" }));
        } else {
            setAuditConfig((prev) => ({ ...prev, entity: "" }));
        }

        if (
            currentConfig.messageType &&
            currentConfig.property &&
            [
                "Rateplan",
                "Roomtype",
                "Taxes",
                "Cancellation Policy",
                "Products",
                "Offer",
            ].includes(currentConfig.messageType)
        ) {
            fetchEntityData(
                currentConfig.property.propertyId,
                currentConfig.messageType,
            );
        }
    }, [
        activeTab,
        pushConfig.messageType,
        pushConfig.property,
        auditConfig.messageType,
        auditConfig.property,
    ]);

    const fetchTestProperties = useCallback(async () => {
        try {
            setTestPropertiesLoading(true);
            const clientId = getClientId();

            if (!clientId) {
                console.error("Client ID not found");
                return;
            }

            const response = await getProperties(clientId, "test");

            if (response.status === 200 && response.data) {
                setTestProperties(
                    response.data.map((property) => ({
                        name: property.propertyName,
                        code: property.propertyCode,
                    })),
                );
                setPropertyOptions(
                    response.data.map((property) => ({
                        ...property,
                        label: `${property.propertyName} (${property.propertyCode})`,
                    })),
                );
            }
        } catch (error) {
            console.error("Error fetching test properties:", error);
        } finally {
            setTestPropertiesLoading(false);
        }
    }, []);

    // Fetch test properties on component mount
    useEffect(() => {
        fetchTestProperties();
    }, [fetchTestProperties]);

    const getClientId = () => {
        return localStorage.getItem("clientInfo")
            ? JSON.parse(localStorage.getItem("clientInfo")).sandboxClientId
            : null;
    };

    const fetchProperties = async (searchString) => {
        try {
            setLoading(true);
            const clientId = getClientId();

            if (!clientId) {
                console.error("Client ID not found");
                return;
            }

            const response = await getProperties(clientId, searchString);

            if (response.status === 200 && response.data) {
                setPropertyOptions(
                    response.data.map((property) => ({
                        ...property,
                        label: `${property.propertyName} (${property.propertyCode})`,
                    })),
                );
            }
        } catch (error) {
            console.error("Error fetching properties:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchEntityData = async (propertyId, messageType) => {
        try {
            setEntityLoading(true);
            let response;
            let mapper;

            switch (messageType) {
                case "Rateplan":
                    response = await getRateplans(propertyId);
                    mapper = (item) => ({
                        value: item.rateplanId,
                        label: `${item.rateplanName} (${item.rateplanCode})`,
                    });
                    break;
                case "Roomtype":
                    response = await getRoomtypes(propertyId);
                    mapper = (item) => ({
                        value: item.roomtypeId,
                        label: `${item.roomtypeName} (${item.roomtypeCode})`,
                    });
                    break;
                case "Taxes":
                    response = await getTaxes(propertyId);
                    mapper = (item) => ({
                        value: item.taxId,
                        label: `${item.taxName} (${item.taxCode})`,
                    });
                    break;
                case "Cancellation Policy":
                    response = await getPolicies(propertyId);
                    mapper = (item) => ({
                        value: item.policyId,
                        label: `${item.policyName} (${item.policyCode})`,
                    });
                    break;
                case "Products":
                    response = await getRateplans(propertyId);
                    mapper = (item) => ({
                        value: item.rateplanId,
                        label: `${item.rateplanName} (${item.rateplanCode})`,
                    });
                    break;
                case "Offer":
                    response = await getRateplans(propertyId);
                    mapper = (item) => ({
                        value: item.rateplanId,
                        label: `${item.rateplanName} (${item.rateplanCode})`,
                    });
                    break;
                default:
                    return;
            }

            if (response.status === 200 && response.data) {
                setEntityOptions(response.data.map(mapper));
            }
        } catch (error) {
            console.error("Error fetching entity data:", error);
        } finally {
            setEntityLoading(false);
        }
    };

    const handleInputChange = (field, value, configType) => {
        if (configType === "push") {
            setPushConfig((prevConfig) => ({
                ...prevConfig,
                [field]: value,
            }));
        } else {
            setAuditConfig((prev) => ({
                ...prev,
                [field]: value,
            }));
        }
    };

    const handlePropertySearch = (event, value, reason, configType) => {
        if (reason === "input" && value && value.length > 2) {
            fetchProperties(value);
        } else if (reason === "input" && (!value || value.length <= 2)) {
            setPropertyOptions([]);
        }
    };

    const handlePropertySelect = (event, value, configType) => {
        handleInputChange("property", value, configType);
    };

    const handleTabChange = (event, newValue) => {
        // Prevent navigation to audit tab if audit API is not supported
        if (newValue === 1 && !isAuditAPISupported) {
            return;
        }
        const tabPath = newValue === 0 ? "push" : "audit";
        navigate(`/api-testing/content-api/${tabPath}`);
        // Reset JSON when switching tabs
        setRequestJSON("");
        setResponse("");
        setResponseMessage({ text: "", status: null });
    };

    const validateRequest = (config) => {
        if (!config.property) {
            return "Please select a property";
        }
        if (!config.messageType) {
            return "Please select a message type";
        }
        if (activeTab === 0 && !config.updateType) {
            return "Please select an update type";
        }
        if (
            activeTab === 0 &&
            ["Rateplan", "Roomtype", "Cancellation Policy"].includes(
                config.messageType,
            ) &&
            ["enable", "disable"].includes(config.updateType)
        ) {
            return `Please select an update type`;
        }
        if (
            [
                "Rateplan",
                "Roomtype",
                "Taxes",
                "Cancellation Policy",
                "Products",
                "Offer",
            ].includes(config.messageType) &&
            !config.entity
        ) {
            return `Please select a ${getEntityLabel(
                config.messageType,
            ).toLowerCase()}`;
        }
        return null;
    };

    const getObjectCode = (entity, messageType) => {
        if (!entity) return "";
        const selectedOption = entityOptions.find(
            (option) => option.value === entity,
        );
        if (!selectedOption) return "";
        return selectedOption.label.split("(")[1].split(")")[0].trim();
    };

    const handleSendRequest = async () => {
        const currentConfig = activeTab === 0 ? pushConfig : auditConfig;
        const validationError = validateRequest(currentConfig);
        if (validationError) {
            alert(validationError);
            return;
        }

        setSubmitLoading(true);
        try {
            const clientInfo = JSON.parse(
                localStorage.getItem("clientInfo") || "{}",
            );
            const idTokenData = JSON.parse(
                localStorage.getItem("idTokenData") || "{}",
            );

            const requestData = {
                propertyId: currentConfig.property.propertyId,
                messageType: getObjectType(
                    currentConfig.messageType,
                ).toUpperCase(),
                objectId:
                    currentConfig.entity || currentConfig.property.propertyId,
                objectCode: currentConfig.entity
                    ? getObjectCode(
                          currentConfig.entity,
                          currentConfig.messageType,
                      )
                    : currentConfig.property.propertyCode,
                clientId: clientInfo.sandboxClientId,
                clientCode: idTokenData.clientCode,
            };

            if (activeTab === 0) {
                requestData.updateType = currentConfig.updateType;
            } else {
                requestData.propertyCode = currentConfig.property.propertyCode;
            }

            setRequestJSON("Waiting for API request...");
            setResponse("Waiting for API response...");
            setResponseMessage({ text: "", status: null });

            const response = await (activeTab === 0
                ? pushContentAPI(requestData)
                : auditContentAPI(requestData));

            // Set response message
            if (response.data) {
                setResponseMessage({
                    text: response.data.message || "",
                    status:
                        response.data.status === "true" ||
                        response.data.status === "SUCCESS",
                });
            }

            updateJsonDisplay(response.data);
        } catch (error) {
            console.error("Error sending request:", error);
            setRequestJSON("No Request Sent");
            setResponse("No Response Received");
            setResponseMessage({
                text:
                    error.message ||
                    "An unexpected error occurred, Please contact admin for further assistance",
                status: false,
            });
        } finally {
            setSubmitLoading(false);
        }
    };

    const updateJsonDisplay = (data) => {
        // Update request JSON
        if (
            !data.requestPayload ||
            ["NULL", "UNDEFINED", "FALSE"].includes(
                String(data.requestPayload).toUpperCase(),
            )
        ) {
            setRequestJSON("No Request Sent");
        } else {
            setRequestJSON(formatJsonData(data.requestPayload));
        }

        // Update response JSON
        if (
            !data.responsePayload ||
            ["NULL", "UNDEFINED", "FALSE"].includes(
                String(data.responsePayload).toUpperCase(),
            )
        ) {
            setResponse("No Response Received");
        } else {
            setResponse(formatJsonData(data.responsePayload));
        }
    };

    // Get the active configuration
    const getActiveConfig = () => (activeTab === 0 ? pushConfig : auditConfig);

    // Common props for API forms
    const apiFormProps = {
        config: getActiveConfig(),
        handleInputChange,
        handlePropertySelect,
        handlePropertySearch,
        loading,
        propertyOptions,
        entityLoading,
        entityOptions,
        messageTypeOptions: filteredMessageTypeOptions,
    };

    return (
        <div className="content-api">
            <TestProperties
                properties={testProperties}
                loading={testPropertiesLoading}
            />

            <div className="api-tabs">
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    TabIndicatorProps={{
                        style: {
                            backgroundColor: "#6366F1",
                        },
                    }}
                >
                    <Tab
                        label="Push API request"
                        className={activeTab === 0 ? "active-tab" : ""}
                        sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#6B7280",
                            "&.Mui-selected": {
                                color: "#6366F1",
                            },
                        }}
                    />
                    <Tab
                        label="Audit API request"
                        disabled={!isAuditAPISupported}
                        className={activeTab === 1 ? "active-tab" : ""}
                        sx={{
                            textTransform: "none",
                            fontSize: "13px",
                            fontWeight: 500,
                            color: isAuditAPISupported ? "#6B7280" : "#D1D5DB",
                            "&.Mui-selected": {
                                color: "#6366F1",
                            },
                            "&.Mui-disabled": {
                                color: "#D1D5DB",
                            },
                        }}
                    />
                </Tabs>
                {!isAuditAPISupported && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: "#6B7280",
                            fontSize: "12px",
                            marginTop: "8px",
                            fontStyle: "italic",
                        }}
                    >
                        Audit API is disabled because it was not selected in the
                        Configure Credentials step.
                    </Typography>
                )}
            </div>

            <Routes>
                <Route
                    path="push"
                    element={<PushApiForm {...apiFormProps} />}
                />
                <Route
                    path="audit"
                    element={<AuditApiForm {...apiFormProps} />}
                />
                <Route
                    path="*"
                    element={
                        <Navigate to="/api-testing/content-api/push" replace />
                    }
                />
            </Routes>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "24px",
                    gap: "16px",
                }}
            >
                <Box sx={{ flex: 1 }} className="response-message">
                    {responseMessage.text && (
                        <Alert
                            severity={
                                responseMessage.status ? "success" : "error"
                            }
                            sx={{
                                "& .MuiAlert-icon": {
                                    padding: "4px 0",
                                },
                                "& .MuiAlert-message": {
                                    fontSize: "14px",
                                    alignContent: "center",
                                },
                            }}
                        >
                            {responseMessage.text}
                        </Alert>
                    )}
                </Box>
                <button className="next-button" onClick={handleSendRequest}>
                    {submitLoading ? (
                        <CircularProgress size={24} color="inherit" />
                    ) : (
                        "Send request"
                    )}
                </button>
            </Box>

            <div className="json-sections">
                <div className="j-section request-section">
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#111827",
                            marginBottom: "8px",
                        }}
                    >
                        Request JSON
                    </Typography>
                    <pre className="json-viewer">
                        {requestJSON ||
                            "Please send request to see request JSON"}
                    </pre>
                </div>
                <div className="j-section response-section">
                    <Typography
                        sx={{
                            fontSize: "13px",
                            fontWeight: 500,
                            color: "#111827",
                            marginBottom: "8px",
                        }}
                    >
                        Response JSON
                    </Typography>
                    <pre className="json-viewer">
                        {response || "Please send request to see response JSON"}
                    </pre>
                </div>
            </div>

            <div className="button-group">
                <button className="back-link" onClick={onBack}>
                    {"<"} Back to ARI Preferences
                </button>
                <button className="next-button" onClick={onFinish}>
                    Finish {">"}
                </button>
            </div>
        </div>
    );
};

export default ContentAPI;
